/** Invisible delimiter between manual and auto tags in DB (ASCII Record Separator). */
export const TAG_AUTO_DELIMITER = '\u001E'
const MAX_STORED_TAGS_LENGTH = 2000

export function normalizeTagKey(tag) {
  return String(tag ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
}

export function parseStoredTags(tagsString) {
  if (!tagsString) {
    return { manual: [], auto: [], all: [] }
  }

  const raw = String(tagsString)
  const hasDelimiter = raw.includes(TAG_AUTO_DELIMITER)

  if (!hasDelimiter) {
    const legacy = raw
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean)
    return { manual: legacy, auto: [], all: legacy }
  }

  const [manualPart = '', autoPart = ''] = raw.split(TAG_AUTO_DELIMITER, 2)
  const manual = manualPart
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)
  const auto = autoPart
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean)

  return { manual, auto, all: [...manual, ...auto] }
}

export function serializeStoredTags(manualTags = [], autoTags = []) {
  const manual = dedupeTagsPreserveOrder(manualTags)
  const manualKeys = new Set(manual.map(normalizeTagKey))
  const auto = dedupeTagsPreserveOrder(autoTags).filter(
    (tag) => !manualKeys.has(normalizeTagKey(tag))
  )

  if (!manual.length && !auto.length) return null

  const manualSerialized = manual.join(', ')
  if (!auto.length) {
    return manualSerialized.slice(0, MAX_STORED_TAGS_LENGTH)
  }

  let keptAuto = [...auto]
  let serialized = `${manualSerialized}${TAG_AUTO_DELIMITER}${keptAuto.join(', ')}`

  while (serialized.length > MAX_STORED_TAGS_LENGTH && keptAuto.length > 0) {
    keptAuto = keptAuto.slice(0, -1)
    serialized = keptAuto.length
      ? `${manualSerialized}${TAG_AUTO_DELIMITER}${keptAuto.join(', ')}`
      : manualSerialized
  }

  return serialized.slice(0, MAX_STORED_TAGS_LENGTH) || null
}

export function dedupeTagsPreserveOrder(tags = []) {
  const seen = new Set()
  const result = []

  for (const raw of tags) {
    const tag = String(raw ?? '').trim()
    if (!tag) continue
    const key = normalizeTagKey(tag)
    if (seen.has(key)) continue
    seen.add(key)
    result.push(tag)
  }

  return result
}

export function parseManualTagsFromRequest(body = {}) {
  if (Array.isArray(body.manualTags)) {
    return dedupeTagsPreserveOrder(body.manualTags)
  }

  if (body.tags === null || body.tags === undefined) {
    return []
  }

  if (Array.isArray(body.tags)) {
    return dedupeTagsPreserveOrder(body.tags)
  }

  if (typeof body.tags === 'string') {
    return dedupeTagsPreserveOrder(
      body.tags.split(',').map((t) => t.trim()).filter(Boolean)
    )
  }

  return []
}

/**
 * TEMP: auto-tag generation paused.
 * Store admin/manual tags only. Extra args kept for call-site compatibility.
 */
export function buildNewsTags(manualTags) {
  return serializeStoredTags(dedupeTagsPreserveOrder(manualTags), [])
}

/** Flat list for search, SEO, and public tag pages — admin/manual tags only. */
export function getAllTagsFromStored(tagsString) {
  return parseStoredTags(tagsString).manual
}
